/**
 * taskDetailLayout — single source of truth for the ClickUp-style task
 * detail drawer's scroll model (P0 punch-list fix: "task detail cut off
 * at bottom").
 *
 * The Projects surface renders under the height-locked shell
 * (`.app-shell--fixed` → `.app-shell__scroll { overflow: hidden }`), so
 * nothing scrolls unless a view owns its own scroll container. The task
 * detail drawer previously scrolled the whole panel (header included)
 * from an ad-hoc wrapper; long tasks (checklists / timeline / linked
 * work below the fold) ended up unreachable.
 *
 * The fix is a three-region drawer, ClickUp-style:
 *   - OVERLAY  → fixed, full-viewport dimmer that hosts the drawer.
 *   - DRAWER   → right-side full-height flex COLUMN (min-height: 0).
 *   - PANEL    → the glass surface, itself a flex column clipped to the
 *                drawer height, so its three children can split roles:
 *       HEADER → pinned (flex: 0 0 auto): title, close, status badges.
 *       BODY   → THE one scroll container (flex: 1 1 auto; min-height: 0;
 *                overflow-y: auto): notes, links, controls, checklist,
 *                meta, timeline — everything below the fold is reachable.
 *       FOOTER → pinned (flex: 0 0 auto): destructive/primary actions
 *                stay visible without scrolling.
 *
 * TASKS_VIEW_SCROLLER applies the same medicine to the Tasks board/list/
 * calendar content itself (also clipped by the fixed Projects shell).
 *
 * Exported as typed style objects (not a stylesheet) so TasksBoardView
 * and the regression tests share one contract — if someone drops
 * `min-height: 0` from the flex chain or moves overflow back onto the
 * whole panel, the tests fail before the operator hits the bug again.
 */

import type { CSSProperties } from 'react';

/** Full-viewport dimmed overlay hosting the right-side drawer. */
export const TASK_DETAIL_OVERLAY: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 80,
  background: 'rgba(6, 6, 12, 0.55)',
  backdropFilter: 'blur(3px)',
  display: 'flex',
  justifyContent: 'flex-end',
};

/**
 * The drawer column: exactly viewport-tall, flex column, min-height 0 so
 * the panel inside can shrink and delegate scrolling to its body region.
 */
export const TASK_DETAIL_DRAWER: CSSProperties = {
  width: 'min(560px, 100%)',
  height: '100%',
  maxHeight: '100vh',
  boxSizing: 'border-box',
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

/**
 * The glass panel: fills the drawer, clips to its rounded corners, and
 * stacks header / body / footer. It must NOT scroll itself — only the
 * body region does — so the header and actions stay pinned.
 */
export const TASK_DETAIL_PANEL: CSSProperties = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  maxHeight: '100%',
  overflow: 'hidden',
  padding: 0,
};

/** Pinned header region (title, close button, badges). */
export const TASK_DETAIL_HEADER: CSSProperties = {
  flex: '0 0 auto',
  padding: '22px 26px 14px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

/** The ONE scroll container inside the drawer — everything below the
 *  fold (checklist, timeline, linked work) is reachable here. */
export const TASK_DETAIL_BODY: CSSProperties = {
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  padding: '4px 26px 20px',
  WebkitOverflowScrolling: 'touch',
};

/** Pinned footer region (destructive / primary actions). */
export const TASK_DETAIL_FOOTER: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: '12px 26px 16px',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
};

/**
 * The Tasks tab's own scroll container. The Projects shell is
 * height-locked (`.app-shell--fixed .app-shell__scroll` is
 * overflow:hidden for the kanban's sake), and
 * `.app-shell--fixed .people-shell--wide > *` pins children to
 * `flex: 0 0 auto` — so without this, the board/list/calendar content
 * below the viewport fold is unreachable. Inline flex/overflow here
 * wins over the stylesheet rule.
 */
export const TASKS_VIEW_SCROLLER: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  paddingRight: 4,
};
